/******************************************
*  Author : Riccardo Di Dato
*  Created On : Thu Dec 30 2021
*  File : Subject.ts
*******************************************/
import { Observable } from "./Observable.js";
import { Observer } from "./Observer.js";
import { Subscription } from "./Subscription.js";
/**
 * Classe che implementa il pattern publisher and subscriber, ispirata ai Subject di RxJS.
 *
 * @example
 * ```ts
 * const sub = new Subject<string>();
 *
 * sub.subscribe(t => console.log(t))
 *
 * sub.next("Ciao")
 * ```
 * In console avremo
 * > "Ciao"
 */
export class Subject {
    observers;
    emitted = false;
    constructor() {
        this.observers = [];
    }
    next(v) {
        this.observers.forEach(o => {
            o.next(v);
        });
    }
    err(err) {
        this.observers.forEach(o => {
            o.error(err);
        });
    }
    complete() {
        if (!this.emitted) {
            this.observers.forEach(o => {
                o.complete();
            });
            this.emitted = true;
        }
    }
    isComplete() {
        return this.emitted;
    }
    /**
     * Callbacks da iscrivere al publisher
     *
     * @param next comportamento in caso di aggiornamento valore corrente
     * @param error comportamento in caso di errore
     * @param complete comportamento in caso di completamento
     */
    subscribe(onNext, onErr = () => { }, onComplete = () => { }) {
        const observer = new Observer(onNext, onErr, onComplete);
        this.observers.push(observer);
        return new Subscription(() => {
            observer.unsubscribe();
            this.observers = this.observers.filter(e => e !== observer);
        });
    }
    /**
     * Permette di applicare una funzione di trasformazione al subject.
     *
     * @note A differenza del `pipe` di RxJS questo applica sempre l'equivalente di un map
     *
     * @example
     * ```ts
     * const sub = new Subject<string>();
     *
     * sub.subscribe(t => console.log(t))
     *
     * sub.next("Ciao")
     * ```
     * In console avremo
     * > "Ciao"
     */
    pipe(onNext) {
        return new Observable((observer) => {
            this.subscribe((ret) => {
                observer.next(onNext(ret));
            }, (err) => {
                observer.error(err);
            }, () => {
                observer.complete();
            });
        });
    }
    lastValueFrom() {
        let resolve, reject, retval;
        const rval = new Promise((innerResolve, innerReject) => {
            resolve = innerResolve;
            reject = innerReject;
        });
        this.subscribe(n => {
            retval = n;
        }, e => {
            reject(e);
        }, () => {
            resolve(retval);
        });
        return rval;
    }
    firstValueFrom() {
        let resolve, reject;
        const rval = new Promise((innerResolve, innerReject) => {
            resolve = innerResolve;
            reject = innerReject;
        });
        this.subscribe(n => {
            resolve(n);
        }, e => {
            reject(e);
        });
        return rval;
    }
}
